/**
 * @fileoverview The Today brief: everything Today decides, worked out by the
 * backend (services/today/todayBrief) and sent as codes with finished
 * sentences in the gardener's language ("B with codes"), so the page only
 * lays it out (the to-do rebuild, batch 5: the app only displays). The
 * heading ("Sparta, TN · Fall"); "Water today?" and its answer, why and
 * where it goes (water.placement); the Almanac (its heading, "Fall almanac ·
 * Raleigh, NC", and the moon and season lines every day, B91) and the frost countdown; the
 * welcome's "This week" line beside it (weekLine) and the Weather page's week
 * judged (weatherWeek: tones, wet days, the sentence; audit batch 4); the
 * weather strip's line; the quiet line; each coming day's sky (its words and icon key), high and line
 * (comingUp.days); and the one to-do list (todo): every job said once,
 * verb first, with its reason, when, Why?, its plants by bed and what is
 * ticked, its Postpone choices, a missed job's choices and a planting job's
 * day in words, for Today's groups, the top of Plants and the Beds page
 * (ticked through api/todo), with the frost or freeze alert (todo.alert) and
 * the one piece of advice for each place (todo.reads: the tips and notices,
 * chosen by the server for the person asking; marked through api/todo), and
 * the week by condition (todo.week.lines: "Too cold Fri–Tue nights · cover
 * the basil and ginger", today's watering last), which the Weather page's
 * garden week and the Plants filter sheet read, and the gardener's own
 * to-dos for the plant and bed cards (todo.own, D50; each own job's own).
 *
 * Every brief says what it was built from (fingerprint) and until when it
 * holds (validUntil, the garden's next midnight): the app reads again then,
 * and a read with the same fingerprint changes nothing (lib/briefFreshness).
 * The brief is for the garden's own day (date), never the phone's.
 *
 * Every said thing carries a stable code, its values (numbers in the
 * gardener's unit, dates as "YYYY-MM-DD") and its text; the app tints and
 * lays out by code and shows the text.
 *
 * Where: TodayPage, PlantGrid, WeatherPage, LocationGrid, AlertBar (the
 * header's frost bar), WeatherPill (frost tonight) through useTodayBrief (getTodayBrief);
 * GardenConditionsBar, PlantFilterSheet, JobCard, TodoGroups, PlantJobs,
 * BedJobs.
 * API: GET /today/brief?lang=&unit=&days=&device= (the garden's postcode and
 * day are the server's); GET /today/brief/reads?lang=&device= (the advice
 * before a postcode).
 *
 * @module api/todayBrief
 */

import { apiClient } from './apiClient';
import type { RuleLine, TodoReason } from './comingUpRules';
import type { SkyKey } from './header';
import type { NamePart } from '@/shared/types/plant';

/** One thing the brief says: its code, the values it was said from, and the finished text. */
export interface BriefSaid {
  /** The dictionary code: "water.yesDrySince", "jobs.cover", "countdown.about.first". */
  code: string;
  /** The raw values: numbers in the gardener's unit, dates as "YYYY-MM-DD", plant words. */
  values: Record<string, unknown>;
  /** The sentence in the requested language. */
  text: string;
}

/** A chip's colour: the verdict tint of its tone; wind, which has no verdict colour, plain. */
export type ChipTone = 'cold' | 'warm' | 'water' | 'wind';

/** "Water today?"'s chip: the rain that fell and today's high, or the water rule's reason. */
export interface BriefChip {
  code: string;
  parts: BriefSaid[];
  text: string;
  tone: ChipTone;
}

/** Grow's answer to "Water today?". */
export interface BriefWaterAnswer extends BriefSaid {
  /** Whether the plan waters any plant today. */
  yes: boolean;
  /** Rain falling now or likely today holds the watering: the answer is Not now or Not yet. */
  held: 'raining' | 'likely' | null;
  /** No: why, from the plan and the rain. */
  why: 'rainToday' | 'fellYesterday' | 'rainSoon' | 'soil' | 'covered' | 'plan' | null;
  /** Yes: the plants the plan waters today, and their words. */
  plantIds: number[];
  plants: string[];
  cantWait: string[];
  rainDate: string | null;
  rainInches: number | null;
  /** No: the next watering day in words ("Next: Sat"), or null. */
  next: BriefSaid | null;
  nextDate: string | null;
  drySince: string | null;
  noRainDays: number | null;
  perDayInches: number | null;
  /** The chip under the answer, or null (none while rain falls or is likely). */
  chip: BriefChip | null;
  /** The line under the answer (why a Yes; that Grow looks again tomorrow), or null. */
  line: BriefSaid | null;
  /** The finger test after yesterday's rain, or null. */
  fingerTest: BriefSaid | null;
  /** The water job is drawn as the answer (one item with its rows). */
  merged: boolean;
}

/** One row of "Water today?": an amount (or a range) and its plants. */
export interface BriefWaterRow extends BriefSaid {
  inches: number | null;
  to: number | null;
  plantIds: number[];
  /**
   * Each plant's name in this row: its crop word (word), and when the same crop sits in two rows, the name
   * as pieces (parts) with what tells it apart in bold (its variety, then its kind, then its bed, then the
   * day it was added: services/grow/plantNaming); text is the whole name.
   */
  plants: Array<{
    id: number;
    word: string;
    text?: string;
    parts?: NamePart[];
  }>;
}

/** "Water today?" as the brief sends it. */
export interface BriefWater {
  /** "Water today?" */
  question: string;
  answer: BriefWaterAnswer;
  rows: BriefWaterRow[];
  /** With this many plants or fewer, every name shows (today.WATER_ALL_NAMES_UP_TO). */
  allNamesUpTo: number;
  reason: TodoReason | null;
  /** Where it goes (the server's): always 'top', first under the Almanac, above the weather card and the to-dos (B152). */
  placement: 'top';
  /** The keys of today's water job(s), drawn under the heading in the order the server gives; they are in todo.jobs but in no group. Empty on a day with none (the answer is then one line). */
  jobKeys: string[];
}

/** A line of the Almanac that falls today (the moon and the turn of the year are the sky's, BriefAlmanacSky, since B91). */
export interface BriefCalendarLine extends BriefSaid {
  key: string;
  kind: 'frost' | 'freeze' | 'hottest' | 'coldest';
  season?: 'first' | 'last';
  /** Where the line leads (a frost or freeze date to that season's dates on Weather), or null. */
  link: string | null;
}

/**
 * The Almanac's moon line (B91), every day: "Waning moon · new moon in 10 days", or on the night a new or full moon is
 * seen, "Full moon tonight, the Hunter's Moon". Everything is the server's (services/today/skyAhead, todayBriefFrom).
 */
export interface BriefAlmanacMoon extends BriefSaid {
  /** The phase of eight, for the icon. */
  phase: 'new' | 'waxing_crescent' | 'first_quarter' | 'waxing_gibbous' | 'full' | 'waning_gibbous' | 'last_quarter' | 'waning_crescent';
  /** How much is lit tonight, 0 to 1. */
  lit: number;
  /** The lit side as the gardener sees it (already flipped south of the equator). */
  litSide: 'left' | 'right';
  /** What to draw: a full disc on a full moon's night, an empty one on a new moon's, else the lit part (lit, litSide). */
  shape: 'full' | 'new' | 'part';
  /** True on the night a new or full moon is seen: the main ink and a gold disc. */
  tonight: boolean;
  /** The next new or full moon: the night it is seen, the days to it, a full moon's name's key. */
  next: { phase: 'new' | 'full'; date: string; days: number; name: string | null } | null;
  /** Tonight's full moon's name's key, or null. */
  name: string | null;
}

/** The Almanac's season line (B91), every day: "Winter solstice Dec 21"; on the day, its own words. */
export interface BriefAlmanacTurn extends BriefSaid {
  /** mar_equinox, jun_solstice, sep_equinox or dec_solstice. */
  key: string;
  /** Its day, "YYYY-MM-DD". */
  date: string;
  /** True on the day itself: the main ink and a gold sun. */
  today: boolean;
}

/** The frost countdown, in the owner's wording. */
export interface BriefCountdown extends BriefSaid {
  season: 'first' | 'last';
  link: string;
  open: boolean;
}

/** The verdict colour family a week line is drawn in; null for wind and the calendar, drawn plain. */
export type WeekLineTone = 'cold' | 'cool' | 'heat' | 'warm' | 'water';

/**
 * One line of the week (the list's todo.week.lines; the server's
 * services/todo/words/week): a built-in condition the week's weather jobs are
 * for, from today over the whole week, finished, or today's watering.
 */
export interface BriefWeekLine {
  /** The condition's line id (grow_care_cold), or "water" for the water line. */
  id: string;
  /** The built-in condition it is about (CARE_COLD, chill...), "water" for the water line, or null for a rule by readings. */
  cond: string | null;
  /** The icon its rule is drawn with. */
  icon: RuleLine['icon'];
  /** The colour family it is drawn in, or null for plain. */
  tone: WeekLineTone | null;
  /** A danger (frost, too cold, too hot): drawn with a warning mark. */
  danger: boolean;
  /** The water job's line, last and quiet. */
  water: boolean;
  /** What the line opens: its plants on Plants, or the forecast on Weather. */
  link: RuleLine['link'];
  /** The plants it is for. */
  plantIds: number[];
  /** The days it fires on, "YYYY-MM-DD", in order. */
  dates: string[];
  /** The day and night halves its condition falls on; empty for days alone. */
  halves: RuleLine['halves'];
  /** The condition with its days from today: "Too cold Fri–Tue nights", "Too hot today and Thu", "Water today". */
  title: string;
  /** What it asks, the plants named or counted: "cover the basil and ginger", "shade 6 plants", "the lettuce and basil"; null for nothing. */
  ask: string | null;
  /** Its rule's advice, or null. */
  tip: string | null;
  /** Its plants' words, each once. */
  plants: string[];
}

/** Each coming day's weather and line, as Coming up draws it under the day (the forecast's days after today). */
export interface BriefComingDay {
  /** The day the line is about, said first by the server ("Tomorrow:", "Thursday:", "Jueves:"; E14), in the garden's own day. */
  day: string;
  /** "Low 37° Saturday night · Rain 0.4″". */
  text: string;
  /** The day's sky in the reader's language, the server's ("Partly cloudy", "Parcialmente nublado"); null for none (W24 Job D). */
  skyText?: string | null;
  /** The day's sky icon key, the server's (SkyIcon draws it); null for none. */
  skyIcon?: SkyKey | null;
  /** The day's high in the reader's unit ("54°F"); null for none. */
  high?: string | null;
}

/**
 * Grow's frost or freeze alert, from the alert's job (the list's): tonight's, else tomorrow night's. A night under a
 * tender crop's kill line above 32°F is said as cold for that crop, never a freeze (B92); the words are the server's.
 */
export interface BriefAlert {
  code: 'PROTECT_FREEZE' | 'PROTECT_FROST';
  plantIds: number[];
  plants: string[];
  low: string | null;
  title: string;
  body: string;
  /** The slim bar's words under the header on every page (AlertBar): "Freeze tomorrow night, 25°F: cover plants tomorrow before dark." */
  bar: string;
  when: 'tonight' | 'tomorrow';
  /**
   * The header pill's icon (WeatherPill): 'snowflake' for a frost or a freeze tonight; null for cold alone ("Too cold
   * for the ginger tonight, 35°F", B92) or tomorrow night's, when the pill shows the sky. The server's, never the app's.
   */
  pillIcon: 'snowflake' | null;
}

/**
 * The welcome's "This week" line, said by the server (services/today/weekLine; audit batch 4, A21). Its own name: it
 * was a second BriefWeekLine, which TypeScript merged with the week's condition line above into one type.
 */
export interface BriefWelcomeWeekLine {
  /** A freezing night, frost, a chilly night, a hot day, or nothing much (mild: the lowest night). */
  kind: 'freeze' | 'frost' | 'chilly' | 'hot' | 'mild';
  /** The day it falls on, "YYYY-MM-DD". */
  date: string;
  /** The reading, in the reader's unit, rounded. */
  temp: number;
  /** The whole line: "This week: chilly Wednesday night, 38°F." */
  text: string;
  /** The part of it drawn bold: "chilly Wednesday night, 38°F". */
  bold: string;
}

/** One day of the Weather page's week, judged by the server (services/today/weatherWeek; A24). */
export interface WeatherWeekDay {
  /** "YYYY-MM-DD". */
  date: string;
  /** The low against its normal. */
  minTone: 'warm' | 'cool' | 'plain';
  /** The high against its normal. */
  maxTone: 'warm' | 'cool' | 'plain';
  /** Wet by Today's rule (a rain day, or a likely chance). */
  wet: boolean;
  /** Its chance of rain is worth showing on its row (weather.RAIN_SHOWN_PCT). */
  chanceShown: boolean;
  /** The day's sky in the reader's language, the server's (W24 Job D); null for none. */
  skyText?: string | null;
  /** The day's sky icon key, the server's (SkyIcon draws it); null for none. */
  skyIcon?: SkyKey | null;
}

/** The Weather page's week, judged: each day's tones and rain, and the week in a sentence. */
export interface WeatherWeek {
  /** "About a normal week, then 8° warmer than normal on Tuesday. Rain likely Wednesday." */
  sentence: string;
  /** The days, in order. */
  days: WeatherWeekDay[];
}

/** The brief. */
export interface TodayBrief {
  success: boolean;
  /** The garden's own day, "YYYY-MM-DD" (its time zone's, never the phone's). */
  date: string;
  lang: string;
  unit: 'F' | 'C';
  headsUpDays: number;
  /** What it was built from, hashed: the same fingerprint means the same brief. */
  fingerprint: string;
  /** The garden's next midnight (an ISO stamp): the brief is read again then. */
  validUntil: string;
  /** The garden's time zone. */
  timeZone: string;
  /** False when the forecast could not be had: To do says so rather than "nothing needs you". */
  hasOutlook: boolean;
  /** C118 F4: the garden has had a plant of its own, ever (the server's gardenStartOf, as the garden place says it): false is a new garden. Today's first step reads this one answer. */
  started: boolean;
  /** The server is rebuilding the garden's outlook as this answers, so the brief may rest on the one before (the app shows "Updating your garden..." and reads again). */
  updating: boolean;
  /** The heading over the date, the Almanac's: "Fall almanac · Raleigh, NC" (B91); the town alone without a season; null with neither. */
  heading: string | null;
  /** "Water today?", or null with nothing in the ground. */
  water: BriefWater | null;
  /** Why nothing needs the gardener today, or null. */
  quiet: { text: string; parts: BriefSaid[] } | null;
  /** The weather strip's second line. */
  strip: { text: string; parts: BriefSaid[] };
  countdown: BriefCountdown | null;
  /** The welcome's "This week" line, or null with no forecast. */
  weekLine: BriefWelcomeWeekLine | null;
  /** The Weather page's week, judged, or null with no forecast. */
  weatherWeek: WeatherWeek | null;
  almanac: {
    calendar: BriefCalendarLine[];
    /** The wettest or driest month's line, said on the month's first opened days (the app's useRainMonth), or null. */
    rainMonth: (BriefSaid & { which: 'wettest' | 'driest'; month: number; opensDays: number }) | null;
    /** The panel's Almanac rules, worded. */
    rules: Array<{ id: string; text: string; tip: string | null }>;
    /** B91: the moon and season lines under the date, each null when switched off (todo_rule) or unknown. */
    sky: { moon: BriefAlmanacMoon | null; turn: BriefAlmanacTurn | null };
  };
  comingUp: {
    /** Each coming day's sky, high and line ("Low 37° Saturday night · Rain 0.4″"), by date: under the day in Coming up. */
    days?: Record<string, BriefComingDay>;
  };
  /** Whether This week (the planner's notes) shows. */
  planner: boolean;
  /** The one to-do list (D51): every job, said once, for Today, the top of Plants and the Beds page. */
  todo: BriefTodo;
  /**
   * The server's one-line note, drawn as the weather card's last line, the whole line a link to `link` (the server's path):
   * B64, a garden whose plants are all archived (todo_word weather.allArchived, link the archive); B137, a started garden
   * of kept seeds and no plants (weather.seedsKept, link the Seed vault). Null otherwise; always sent (C118 F3).
   */
  note: { text: string; kind: 'allArchived' | 'seedsKept'; link: string } | null;
}

/**
 * A When you can job's Postpone choices (owner, 2026-10-17): the days offered ("Tomorrow — Sunday", "In a week —
 * Saturday, Oct 24", "In its last week — Sunday, Oct 25") and a day to pick within its window.
 */
export interface TodoPostpone {
  /** "Postpone: Plant the onion sets". */
  title: string;
  /** "You can do it until Oct 31.", or null for a job with no end. */
  sub: string | null;
  /** "It goes on Today on that day." */
  note: string;
  /** A day to postpone to, or (action whenever) When you can itself: off Today and Coming up until a good day. */
  options: Array<{ day?: string; action?: 'whenever'; text: string; sub: string | null }>;
  /** "Pick a day…", "Any day until Oct 31", and the days it may be; null when it offers no day to pick. */
  pick: { text: string; sub: string | null; min: string; max: string } | null;
}

/** A job's "when": its words and its pill's colour. */
export interface TodoWhen {
  /**
   * today, tonight, tomorrow, inDays or thisWeek; until or thisSeason for a When you can job; for a gardener's own
   * to-do (D50) also lastDay ("Last day", a "this week" one's last day), since ("Since Oct 20", one carried a week) and
   * onDay (a day further on).
   */
  code: 'today' | 'tonight' | 'tomorrow' | 'inDays' | 'thisWeek' | 'until' | 'thisSeason' | 'lastDay' | 'since' | 'onDay';
  /** "Today", "In 2 days", "Until Oct 31". */
  text: string;
  /** The pill's colour: today's green, the cold blue, the calm amber, and a When you can job's quiet grey. */
  tone: 'today' | 'cold' | 'soon' | 'calm';
}

/** One tickable row in a job's plant list: one plant, or several alike ("Peppers (3)"). */
export interface TodoPlantRow {
  /** The plants it ticks. */
  ids: number[];
  /** Its name in pieces, with what tells it apart in bold, and after it a muted note (kind 'note') when the job has one: " · about ½″" on a water card. */
  parts: NamePart[];
  /** Its name as one string. */
  text: string;
  /** The first plant's card. */
  link: string;
  /** Every plant in it is ticked. */
  done: boolean;
}

/** A job's plants, by bed, with the words about them. */
export interface TodoPlants {
  ids: number[];
  count: number;
  /** The button's words, the same open and closed: "9 plants in 2 beds". */
  open: string;
  /** By bed; a bed's note ("Run the soaker hose or drip."), said with its bed, null or absent for none. */
  beds: Array<{ id: number | null; name: string; count: number; heading: string; note?: string | null; rows: TodoPlantRow[] }>;
  /** What a plant is when ticked: "covered". */
  what: string;
  /** "Mark all covered". */
  markAll: string;
  doneCount: number;
  /** "2 of 9 covered". */
  doneText: string;
}

/** One job on the list. */
export interface TodoJob {
  /** Unique and stable: what it asks and its first day ("cover|2026-10-18"). */
  key: string;
  /** What it asks, as its ticks are kept ("cover@12" for a bed's, D55). */
  base: string;
  /** Its kind: cover, bringIn, layer, heater, uncover, vent, shade, stake, water, timerOff, harvest, plant, rule... */
  kind: string;
  /** A plant job shows on Plants too; a garden chore on Today only. */
  family: 'plant' | 'garden';
  /** Keeps a plant from harm: first within its day. */
  danger: boolean;
  /** A frost, freeze or cold cover: the blue left edge (owner, 2026-10-15). */
  edge: boolean;
  /** The group it is listed under. */
  group: 'yesterday' | 'today' | 'coming' | 'whenever';
  /** The days (or nights) it is for, "YYYY-MM-DD". */
  dates: string[];
  /** The day it is due. */
  due: string;
  /** Days from today to its due day. */
  inDays: number;
  /** Today's Coming up shows it (within the gardener's Coming up days). */
  onToday: boolean;
  /** Verb first: "Cover 9 plants". */
  title: string;
  /** One line: "Sun night freeze, 29°F"; null for none. */
  reason: string | null;
  when: TodoWhen;
  /** The calm time-left bar under a coming job: so many segments, so many lit. */
  left: { of: number; on: number } | null;
  /** A panel rule's advice, on the card's face under the reason ("Scatter it over empty beds and rake it in."); null or absent for none. */
  tip?: string | null;
  /** The why lines, first inside the card's one disclosure: the readings over a run, what to do, the advice. Nothing the reason line or the plant list says. */
  why: string[];
  /** Where the job is done (the photos waiting), or null. */
  link: string | null;
  /** The bed's covers checked against the weather (D55): ok "✓ Your light frost cloth is enough for tonight", short (not enough, and what to do) or a note ("Weigh the cover down"); none for most jobs. */
  checks: Array<{ tone: 'ok' | 'short' | 'note'; text: string }>;
  /** Its bed when the job is a bed's (D55). */
  bed?: { id: number; name: string } | null;
  /** The beds it is shown on, on the Beds page (D56): its own, the ones it names, its plants'; [] for a garden chore (Today only). */
  bedIds: number[];
  /** Its plants. */
  plantIds: number[];
  /** Its plants by bed, with their ticks; null for a job with none. */
  plants: TodoPlants | null;
  /** Ticked, or skipped today. */
  done: boolean;
  /** A When you can job (a planting window, a once-a-season chore), wherever it shows today. */
  calm: boolean;
  /** Its Postpone's choices of day; null or absent: Postpone moves it to tomorrow. */
  postpone?: TodoPostpone | null;
  /** Skipped today: shown in place, struck through, with its line ("Skipped. Grow won't ask about this one again.") and Undo (unskip). */
  skipped: boolean;
  /** What a tick wrote: "Covered, noted on all 9 plants."; null for none. */
  doneLine: string | null;
  /** Left from yesterday and still worth doing: a tap offers its missedChoices. */
  missed: boolean;
  /** A missed job's choices, with their words: do it today, postpone it to tomorrow, skip it; null for any other job. */
  missedChoices?: Array<{ action: 'today' | 'tomorrow' | 'skip'; text: string; sub: string | null }> | null;
  /** Moved here from another day. */
  moved: boolean;
  /**
   * A planting job: what a tick records ("Sown"), the day it was done and that day in words ("Today", "Oct 3"), and the
   * days its date box may be moved within (notBefore, the plants' last stage; latest, the garden's today). askBed (D69):
   * the job's plants in the Seed vault on a Sow outside or Plant out job, which the tick asks a bed for ("Which bed did
   * they go into?"); empty for none and for Start indoors.
   */
  planting: {
    method: string; methodKey: string; stageId: number | null; done: string; on: string | null;
    onText?: string | null; latest?: string | null; notBefore: string | null; askBed?: number[];
  } | null;
  /** The Plants page's filter for its plants: "Showing the 9 to cover". */
  filter: { text: string; plantIds: number[] } | null;
  /** A job for the night. */
  night: boolean;
  /** A job for any day this week (a planting window). */
  week: boolean;
  /** Its circle can be ticked now. */
  tickable: boolean;
  /** Its plants can be ticked one by one. */
  perPlant: boolean;
  /** A gardener's own to-do's (kind own, D50): which it is, what it is about, who added it, and its planned when. */
  own?: OwnTodoJob | null;
}

/** What a gardener's own to-do carries on its card (D50): for its Edit, and who added it. */
export interface OwnTodoJob {
  /** own_todo_id: what Edit changes. */
  id: number;
  about: 'garden' | 'plant' | 'bed';
  plantObjId: number | null;
  locationId: number | null;
  madeBy: number | null;
  madeByName: string | null;
  /** Another member's first name when they added it; null when the reader did. */
  addedBy: string | null;
  /** The when the edit sheet shows picked (the server's: a to-do whose day has passed shows Today). */
  planned: { when: 'today' | 'week' | 'day'; day: string | null };
  createdAt: string | null;
  /** E23: a planting to-do's Seed vault plant its tick moves out (the server's; asked which bed first); [] for none. */
  askBed: number[];
  /** B140: the to-do's job (a code on the shared Jobs list), or null for none; the Edit sheet rings it. */
  job: string | null;
}

/**
 * One open own to-do as the plant and bed cards list it (the brief's todo.own, D50), newest first: the compact row's
 * circle, title and pill, and the edit sheet's planned when. Its key is the mark's.
 */
export interface OwnTodoItem {
  key: string;
  id: number;
  title: string;
  when: TodoWhen;
  due: string;
  /** later: further ahead than Coming up, on its card only. */
  group: 'today' | 'coming' | 'whenever' | 'later' | 'yesterday';
  about: 'garden' | 'plant' | 'bed';
  plantObjId: number | null;
  locationId: number | null;
  calm: boolean;
  planned: { when: 'today' | 'week' | 'day'; day: string | null };
  done: boolean;
  skipped: boolean;
  tickable: boolean;
  addedBy: string | null;
  madeBy: number | null;
  madeByName: string | null;
  /** E23: a planting to-do's gentle line ("Grow suggests planting mid to late February"), the server's; null for none
   * (and once it is ticked or skipped). */
  tip: string | null;
  /** E23: a planting to-do's Seed vault plant its tick moves out (the server's; asked which bed first); [] for none. */
  askBed: number[];
  /** B140: the to-do's job (a code on the shared Jobs list), or null for none. */
  job: string | null;
}

/** One of Today's groups: From yesterday, Today, Coming up. */
export interface TodoGroup {
  name: 'yesterday' | 'today' | 'coming' | 'whenever';
  /** Under the heading: When you can's promise ("No rush. Grow puts each on Today on a good day for it, and in its last week."). */
  note?: string;
  title: string;
  /** Its jobs, in order. */
  keys: string[];
  /** "3 jobs", "1 of 2 done". */
  count: string;
  /** "3 more this week", or null. */
  more: string | null;
  allDone: boolean;
}

/** A job as one bed's card shows it, when its plants are in other beds too (the server's bedViewOf). */
export interface BriefBedView {
  /** This bed's plants of the job: what a tick of the whole card writes from here. */
  plantIds: number[];
  /** They are all done (or the whole job is). */
  done: boolean;
  /** The job titled by this bed's plants ("Water the calendulas"), when its title names or counts them. */
  title?: string;
  /** Its reason for this bed's plants alone ("About ½″ — dry since Sun"); null for none; absent keeps the job's. */
  reason?: string | null;
  /** The job's plants, this bed's alone: "2 plants here", their rows, "1 of 2 watered". */
  plants: TodoPlants;
}

/** One bed on the Beds page, as the server orders them (D56). */
export interface BriefBed {
  /** The bed (location id). */
  id: number;
  /** Its jobs, in the list's order (by day, danger first), done ones in place. */
  keys: string[];
  /** "2 jobs": the open bed's This week. */
  count: string;
  /** How many of them are not done: more than none, the bed needs the gardener. */
  open: number;
  /**
   * Its part of each job whose plants are in other beds too, by key (owner, 2026-10-16: "2 plants here"): its plants
   * here, the ids a tick from this bed's card writes, and whether they are all done. A job all in this bed has none.
   */
  views: Record<string, BriefBedView>;
  /** A bed with no job: "Nothing needs you this week"; indoors, "Indoors: no weather jobs"; null when it has jobs. */
  quiet: string | null;
  /** "2 more jobs" past the ones its card shows (BriefTodo.shown); null for none. */
  more: string | null;
  /**
   * The gardener's own to-dos about this bed (B158 job 7), counted by the server (services/todo/bedList): `open` is how many are
   * not done or skipped (the To-dos tile's count), `dueToday` is whether one is in Today's group (the tile is tinted).
   */
  todos: { open: number; dueToday: boolean };
}

/** The Beds page's part of the list (D56): the beds in order, the ones that need the gardener first. */
export interface BriefBeds {
  /** "4 beds · 2 need you". */
  count: string;
  /** Every bed in the garden, in order. */
  list: BriefBed[];
}

/** Where a piece of advice goes (the server places one at each). */
export type ReadPlace = 'notice' | 'weather' | 'postcode_step' | 'empty_garden' | 'after_add' | 'bottom_panel';

/** Something the gardener does that finishes advice, told to the server when the brief's readEvents lists it. */
export type ReadEvent = 'plant_added' | 'photos_opened' | 'installed';

/** What a piece of advice's button does (the app's part: the screen it is on). */
export type ReadAction = 'add_plant' | 'add_photo' | 'open_photos' | 'install' | 'subscribe' | 'share' | 'open_link';

/**
 * One piece of advice, chosen for its place by the server (a tip or a notice: a rule that asks nothing to tick; the
 * to-do rebuild, batch 5): its words with the gardener's name put in ({plant} and {location} are the moment's, put in
 * by the screen that shows it), its button, and how it is put away.
 */
export interface TodoRead {
  /** Its key for a mark ("read:first_plant|2026-10-17"). */
  key: string;
  /** "read:first_plant". */
  base: string;
  /** The rule's id ("first_plant"). */
  id: string;
  place: ReadPlace;
  /** The heading; '' for none. */
  title: string;
  /** The line; '' for none. */
  body: string;
  /** Its button: the words, what it does, and for open_link where it goes; null for none. */
  button: { text: string; does: ReadAction | null; url: string | null } | null;
  /** How it is put away: for good (until), for today (daily), or not at all (never). */
  putAway: 'until' | 'daily' | 'never';
  /** A notice that cannot be put away: at the very top of Today, in the alert's colour. */
  urgent: boolean;
  /** How it comes back: every day (every), said once, until done, or weekly (a week after it is put away). */
  repeats: 'every' | 'once' | 'until_done' | 'weekly' | 'season';
  /** What pressing its button marks: 'put_away' for weekly advice (it comes back next week), else 'done' (the server decides). */
  buttonMark: 'put_away' | 'done';
  /** What the gardener does that finishes it; null for none. */
  doneWhen: ReadEvent | null;
  /** Already said to the server today (seen); the app says it once a day otherwise. */
  seenToday: boolean;
}

/**
 * One job on the shared Jobs list, as the brief's todo.jobLists sends it (B140): its code, its pill word in the reader's
 * language and its palette colour key. (Not TodoJob: that is a card on Today's list.)
 */
export interface JobListItem {
  code: string;
  label: string;
  color_key: string;
}

/** The one to-do list. */
export interface BriefTodo {
  /** The garden's day the list is for, "YYYY-MM-DD". */
  today: string;
  /** What the brief was built from, hashed (the brief's own). */
  fingerprint: string | null;
  /** The garden's next midnight (the brief's own). */
  validUntil: string | null;
  /** Jobs shown in a group before "3 more this week". */
  shown: number;
  jobs: TodoJob[];
  /** B140: the shared Jobs list by list type (plant, bed, garden...), each job in the owner's order, in the reader's language; a type with no job is []. */
  jobLists: Record<string, JobListItem[]>;
  /** B78: for a garden that has not started, the key of the "Sort your photos" job to draw above the start choices (photos are waiting); null for none; always sent (C118 F3). */
  startJob: string | null;
  /** whenever: When you can (owner, 2026-10-17). */
  groups: { yesterday: TodoGroup; today: TodoGroup; coming: TodoGroup; whenever: TodoGroup };
  /** The top of Plants: the plant jobs, danger first. */
  plants: { title: string; keys: string[]; count: string | null; more: string | null; chores: { count: number; text: string; link: string } | null };
  /** The Beds page (D56): each bed's jobs, in the server's order. */
  beds: BriefBeds;
  /**
   * Jobs postponed today, named: one, "Plant the onion sets · postponed to Oct 25 · Bring back"; several, "2 postponed ·
   * Show", then each with its own Bring back (items).
   */
  moved: {
    keys: string[]; text: string | null; back: string; show: string; items: Array<{ key: string; title: string; text: string }>;
    /** A job postponed to today, or waiting under When you can, that is no longer needed: one line each, said once (owner, 2026-10-17, answer 1). */
    gone?: Array<{ key: string; text: string }>;
  } | null;
  /** Grow's frost or freeze alert, from the alert's job; null for none. */
  alert?: BriefAlert | null;
  /**
   * The week by condition, from the list's own weather (the server's words/week): the Plants filter sheet's "This week"
   * chips and the Weather page's garden week; the water line last.
   */
  week: { lines: BriefWeekLine[] };
  /** The one piece of advice for each place, the server's choice for the person asking. */
  reads: Partial<Record<ReadPlace, TodoRead>>;
  /** Every open own to-do for the plant and bed cards' "Your to-dos" rows (D50); absent when the garden has none. */
  own?: OwnTodoItem[];
  /** The events that would finish advice not yet done: the app tells the server of one only when it is listed. */
  readEvents: ReadEvent[];
  words: {
    why: string; fewer: string; showAll: string; undo: string; nothing: string; allDone: string;
    later: { today: string; tomorrow: string; tomorrowSub: string; skip: string; skipSub: string };
  };
}

/**
 * The Today brief for the garden being tended (its owner's postcode and day,
 * the server's): in the reader's language and unit, Coming up as far ahead as
 * asked.
 *
 * @param lang - The words' language.
 * @param unit - The readings' unit.
 * @param days - How far ahead Coming up looks; null leaves it to the server (the gardener's own, else the Grow setting's).
 * @param device - The device the app is on, for the advice about it.
 * @returns The brief.
 */
export async function getTodayBrief(lang: string, unit: 'F' | 'C', days: number | null, device: Device): Promise<TodayBrief> {
  const response = await apiClient.get<TodayBrief>('/today/brief', { params: { lang, unit, days, device } });
  return response.data;
}

/** The device the app is on, for advice about it (the install tips). */
export type Device = 'android' | 'iphone' | 'computer' | 'home_screen_app';

/** The person's advice on its own: before the garden has a postcode, when there is no brief. */
export interface BriefReads {
  success: boolean;
  /** The garden's day. */
  date: string;
  reads: Partial<Record<ReadPlace, TodoRead>>;
  readEvents: ReadEvent[];
}

/**
 * The advice for the person asking without the brief (GET /today/brief/reads): before the garden has a postcode,
 * the postcode step's words and a beta tester's or founding gardener's line.
 *
 * @param lang - The words' language.
 * @param device - The device the app is on.
 * @returns The advice for each place, and the events that would finish some.
 */
export async function getBriefReads(lang: string, device: Device): Promise<BriefReads> {
  const response = await apiClient.get<BriefReads>('/today/brief/reads', { params: { lang, device } });
  return response.data;
}
